(() => {
  if (
    customElements.get(
      'product-cart-notification'
    )
  ) {
    return;
  }

  class ProductCartNotification extends HTMLElement {
    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.restoreHeaderCartButton();
      this.initialized = false;
    }

    async initialize() {
      await customElements.whenDefined(
        'drawer-element'
      );

      if (
        !this.isConnected ||
        this.initialized
      ) {
        return;
      }

      this.form = document.getElementById(
        this.dataset.productFormId
      );

      this.formId =
  this.form?.getAttribute('id') ||
  this.dataset.productFormId;

      this.drawer = this.querySelector(
        'drawer-element'
      );

      this.itemsContainer = this.querySelector(
        '[data-notification-items]'
      );

      this.itemTemplate = this.querySelector(
        '[data-notification-item-template]'
      );

      if (
        !this.form ||
        !this.drawer ||
        !this.itemsContainer ||
        !this.itemTemplate
      ) {
        return;
      }

      this.initialized = true;
      this.abortController =
        new AbortController();

      const signal =
        this.abortController.signal;

      this.form.addEventListener(
        'submit',
        this.onSubmit.bind(this),
        {
          capture: true,
          signal,
        }
      );

      this.drawer.addEventListener(
        'drawer:afterHide',
        this.onDrawerAfterHide.bind(this),
        {
          signal,
        }
      );

      window.addEventListener(
        'resize',
        this.onResize.bind(this),
        {
          signal,
        }
      );
    }

    get submitButtons() {
      const internalButtons = Array.from(
        this.form.querySelectorAll(
          '[type="submit"]'
        )
      );

      const externalButtons = Array.from(
        document.querySelectorAll(
          '[type="submit"][form]'
        )
      ).filter(
        (button) =>
          button.getAttribute('form') ===
          this.formId
      );

      return Array.from(
        new Set([
          ...internalButtons,
          ...externalButtons,
        ])
      );
    }

    async onSubmit(event) {
      if (this.redirectingToCheckout) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const submitter = event.submitter;

      if (
        submitter &&
        (
          submitter.disabled ||
          submitter.getAttribute(
            'aria-disabled'
          ) === 'true'
        )
      ) {
        return;
      }

      if (
        submitter &&
        submitter.name &&
        submitter.name !== 'add'
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isSubmitting) return;

      this.isSubmitting = true;
      this.itemWasAdded = false;

      this.activeElement =
        submitter ||
        this.form.querySelector(
          '[type="submit"]'
        );

      const buttons = this.submitButtons;

      const buttonStates = buttons.map(
        (button) => ({
          button,
          ariaDisabled:
            button.getAttribute(
              'aria-disabled'
            ),
          ariaBusy:
            button.getAttribute(
              'aria-busy'
            ),
        })
      );

      buttons.forEach((button) => {
        button.setAttribute(
          'aria-disabled',
          'true'
        );

        button.setAttribute(
          'aria-busy',
          'true'
        );
      });

      this.clearError();

      try {
        const formData =
          new FormData(this.form);

        const requestedItems =
          this.buildRequestedItems(formData);

        const addedItems =
          await this.addToCart(
            requestedItems
          );

        this.itemWasAdded = true;

        if (this.form.dataset.skipCart === 'true') {
          theme.utils.goToCheckout();
          this.redirectingToCheckout = true;
          return;
        }

        const cart =
          await this.getUpdatedCart();

        this.updateThemeCart(cart);

        await this.populateNotification(addedItems, requestedItems, cart);

        this.prepareDrawerPosition();

        this.drawer.classList.remove(
          'is-content-ready'
        );

        requestAnimationFrame(() => {
          this.drawer.classList.add(
            'is-content-ready'
          );

          this.drawer.show(
            this.activeElement
          );
        });
      } catch (error) {
        if (this.itemWasAdded) {
          window.location.assign(
            this.dataset.cartUrl
          );

          return;
        }

        this.showError(
          error.message ||
            'Unable to add these items to the cart.'
        );
      } finally {
        this.isSubmitting = false;

        if (!this.redirectingToCheckout) {
          buttonStates.forEach((state) => {
            if (state.ariaDisabled === null) {
              state.button.removeAttribute('aria-disabled');
            } else {
              state.button.setAttribute('aria-disabled', state.ariaDisabled);
            }

            if (state.ariaBusy === null) {
              state.button.removeAttribute('aria-busy');
            } else {
              state.button.setAttribute('aria-busy', state.ariaBusy);
            }
          });
        }
      }
    }

    buildRequestedItems(formData) {
      const mainItem = {
        id: Number(formData.get('id')),
        quantity: Math.max(
          1,
          Number.parseInt(
            formData.get('quantity'),
            10
          ) || 1
        ),
        isMain: true,
      };

      if (!mainItem.id) {
        throw new Error(
          'Please select an available product option.'
        );
      }

      const properties = {};

      formData.forEach((value, key) => {
        const match = key.match(
          /^properties\[(.+)\]$/
        );

        if (
          match &&
          value !== ''
        ) {
          properties[match[1]] = value;
        }
      });

      if (
        Object.keys(properties).length
      ) {
        mainItem.properties = properties;
      }

      const sellingPlan =
        formData.get('selling_plan');

      if (sellingPlan) {
        mainItem.selling_plan =
          Number(sellingPlan) ||
          sellingPlan;
      }

      const items = [mainItem];

      const addOn = Array.from(
        document.querySelectorAll(
          'product-add-on[data-product-form-id]'
        )
      ).find(
        (element) =>
          element.dataset.productFormId ===
          this.formId
      );

      const addOnItem =
        addOn?.cartItem;

      if (addOnItem) {
        items.push({
          id: Number(addOnItem.id),
          quantity: Math.max(
            1,
            Number(addOnItem.quantity) || 1
          ),
          isMain: false,
        });
      }

      return items;
    }

    async addToCart(requestedItems) {
      const items = requestedItems.map(
        (item) => {
          const cartItem = {
            id: item.id,
            quantity: item.quantity,
          };

          if (item.properties) {
            cartItem.properties =
              item.properties;
          }

          if (item.selling_plan) {
            cartItem.selling_plan =
              item.selling_plan;
          }

          return cartItem;
        }
      );

      const response = await fetch(
        this.dataset.cartAddUrl,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type':
              'application/json',
            'X-Requested-With':
              'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items,
          }),
        }
      );

      const result =
        await response.json();

      if (
        !response.ok ||
        result.status
      ) {
        throw new Error(
          result.description ||
            result.message ||
            'Unable to add these items to the cart.'
        );
      }

      return Array.isArray(result.items)
        ? result.items
        : [result];
    }

    async getUpdatedCart() {
      const response = await fetch(
        this.dataset.cartJsonUrl,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          cache: 'no-store',
        }
      );

      if (!response.ok) {
        throw new Error(
          'Unable to update the cart.'
        );
      }

      return response.json();
    }

    updateThemeCart(cart) {
      if (
        window.theme?.pubsub &&
        window.theme?.pubsub
          ?.PUB_SUB_EVENTS
      ) {
        window.theme.pubsub.publish(
          window.theme.pubsub
            .PUB_SUB_EVENTS.cartUpdate,
          {
            cart,
          }
        );
      }

      document.dispatchEvent(
        new CustomEvent(
          'ajaxProduct:added',
          {
            detail: {
              cart,
            },
          }
        )
      );
    }

   async populateNotification(addedItems, requestedItems, cart) {
      const subtotal =
        this.drawer.querySelector(
          '[data-notification-subtotal]'
        );

      const cartCount =
        this.drawer.querySelector(
          '[data-notification-cart-count]'
        );

      const availableAddedItems = [
        ...addedItems,
      ];

      const promoBadges =
        this.getPromoBadges();

      this.itemsContainer.replaceChildren();

      requestedItems.forEach(
        (requestedItem) => {
          const addedIndex =
            availableAddedItems.findIndex(
              (item) =>
                Number(
                  item.variant_id ||
                    item.id
                ) ===
                Number(requestedItem.id)
            );

          const addedItem =
            addedIndex >= 0
              ? availableAddedItems.splice(
                  addedIndex,
                  1
                )[0]
              : {
                  id: requestedItem.id,
                  variant_id:
                    requestedItem.id,
                };

          const displayItem =
            this.findAddedCartItem(
              addedItem,
              cart
            ) || addedItem;

          const productTitle =
            displayItem.product_title ||
            addedItem.product_title ||
            displayItem.title ||
            addedItem.title ||
            '';

          const row =
            this.itemTemplate.content
              .firstElementChild
              .cloneNode(true);

          this.populateImage(
            row.querySelector(
              '[data-notification-image]'
            ),
            displayItem,
            productTitle,
            requestedItem.isMain
          );

          row.querySelector(
            '[data-notification-title]'
          ).textContent = productTitle;

          row.querySelector(
            '[data-notification-added-quantity]'
          ).textContent =
            `+${requestedItem.quantity}`;

          this.populateOptions(
            row.querySelector(
              '[data-notification-options]'
            ),
            displayItem
          );

          this.populateBadges(
            row.querySelector(
              '[data-notification-item-badges]'
            ),
            requestedItem.isMain
              ? promoBadges
              : []
          );

          this.itemsContainer.appendChild(
            row
          );
        }
      );

     this.drawer.dataset.notificationItemCount = requestedItems.length;
subtotal.textContent = this.formatCartTotal(cart);
cartCount.textContent = cart.item_count;

await this.updateCartSavings(cart);
      }

    getPromoBadges() {
      const productInfo =
        this.closest('.product__info');

      if (!productInfo) return [];

      const badgesContainer =
        productInfo.querySelector(
          '[data-product-promo-badges], .product-promo-badges'
        );

      if (!badgesContainer) return [];

      return Array.from(
        badgesContainer.querySelectorAll(
          '.product-promo-badges__badge'
        )
      );
    }

    populateBadges(
      container,
      sourceBadges
    ) {
      if (!container) return;

      container.replaceChildren();

      sourceBadges.forEach((badge) => {
        container.appendChild(
          badge.cloneNode(true)
        );
      });

      container.hidden =
        sourceBadges.length === 0;
    }

    findAddedCartItem(
      addedItem,
      cart
    ) {
      if (!Array.isArray(cart.items)) {
        return null;
      }

      if (addedItem.key) {
        const itemByKey =
          cart.items.find(
            (cartItem) =>
              cartItem.key ===
              addedItem.key
          );

        if (itemByKey) {
          return itemByKey;
        }
      }

      const variantId =
        addedItem.variant_id ||
        addedItem.id;

      return variantId
        ? cart.items.find(
            (cartItem) =>
              Number(
                cartItem.variant_id ||
                  cartItem.id
              ) === Number(variantId)
          ) || null
        : null;
    }

    populateImage(
      imageElement,
      item,
      productTitle,
      isMain
    ) {
      if (!imageElement) return;

      const featuredImage =
        item.featured_image;

      let imageSource = '';

      if (
        typeof featuredImage ===
        'string'
      ) {
        imageSource = featuredImage;
      } else if (featuredImage) {
        imageSource =
          featuredImage.url ||
          featuredImage.src ||
          '';
      }

      if (!imageSource) {
        if (
          typeof item.image ===
          'string'
        ) {
          imageSource = item.image;
        } else if (item.image) {
          imageSource =
            item.image.url ||
            item.image.src ||
            '';
        }
      }

      if (
        !imageSource &&
        isMain
      ) {
        imageSource =
          this.getCurrentGalleryImage();
      }

      if (!imageSource) {
        imageElement.hidden = true;
        imageElement.removeAttribute('src');
        imageElement.alt = '';

        return;
      }

      imageElement.src =
        this.resizeImageUrl(
          imageSource,
          480
        );

      imageElement.alt =
        featuredImage?.alt ||
        item.image?.alt ||
        productTitle;

      imageElement.hidden = false;
    }

    getCurrentGalleryImage() {
      const gallery = Array.from(
        document.querySelectorAll(
          'media-gallery[form]'
        )
      ).find(
        (element) =>
          element.getAttribute('form') ===
          this.formId
      );

      if (!gallery) return '';

      const image =
        Array.from(
          gallery.querySelectorAll('img')
        ).filter((candidate) => {
          const media =
            candidate.closest(
              '[data-media-id]'
            ) || candidate;

          const style =
            window.getComputedStyle(media);

          const rect =
            media.getBoundingClientRect();

          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            rect.width > 0 &&
            rect.height > 0
          );
        })[0] ||
        gallery.querySelector('img');

      return (
        image?.currentSrc ||
        image?.src ||
        ''
      );
    }

    populateOptions(
      container,
      item
    ) {
      if (!container) return;

      const options = Array.isArray(
        item.options_with_values
      )
        ? item.options_with_values.filter(
            (option) =>
              option.value &&
              option.value !==
                'Default Title' &&
              option.name !== 'Title'
          )
        : [];

      container.replaceChildren();
      container.hidden =
        options.length === 0;

      options.forEach((option) => {
        const line =
          document.createElement('p');

        line.textContent =
          `${option.name}: ${option.value}`;

        container.appendChild(line);
      });
    }

    async updateCartSavings(cart) {
  const savingsElement = this.drawer.querySelector('[data-cart-total-savings]');
  const savingsValue = savingsElement?.querySelector('[data-cart-total-savings-value]');

  if (!savingsElement || !savingsValue) return;

  const totalSavings = await this.calculateCartSavings(cart);

  if (!this.isConnected) return;

  savingsElement.hidden = totalSavings <= 0;
  savingsValue.textContent = totalSavings > 0
    ? `-${this.formatMoney(totalSavings, cart)}`
    : '';
}

async calculateCartSavings(cart) {
  const items = Array.isArray(cart.items) ? cart.items : [];

  const compareAtSavings = await Promise.all(
    items.map(async (item) => {
      const compareAtPrice = await this.getCompareAtPrice(item);
      const originalPrice = Number(item.original_price ?? item.price ?? 0);
      const quantity = Math.max(0, Number(item.quantity || 0));

      return Math.max(0, compareAtPrice - originalPrice) * quantity;
    })
  );

  const shopifyDiscountSavings = Math.max(
    0,
    Number(cart.total_discount || 0)
  );

  return shopifyDiscountSavings
    + compareAtSavings.reduce((total, saving) => total + saving, 0);
}

async getCompareAtPrice(item) {
  const directCompareAtPrice = Number(
    item.compare_at_price
    || item.variant?.compare_at_price
    || 0
  );

  if (directCompareAtPrice > 0) return directCompareAtPrice;

  const product = await this.getProductJson(item);
  const variantId = Number(item.variant_id || item.id);

  const variant = product?.variants?.find(
    (candidate) => Number(candidate.id) === variantId
  );

  return Math.max(0, Number(variant?.compare_at_price || 0));
}

async getProductJson(item) {
  const productUrl = this.getProductJsonUrl(item);

  if (!productUrl) return null;

  this.productJsonCache ||= new Map();

  if (!this.productJsonCache.has(productUrl)) {
    const request = fetch(productUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    })
      .then((response) => response.ok ? response.json() : null)
      .catch(() => null);

    this.productJsonCache.set(productUrl, request);
  }

  return this.productJsonCache.get(productUrl);
}

getProductJsonUrl(item) {
  try {
    const root = window.Shopify?.routes?.root || '/';

    const source = item.url || (
      item.handle
        ? `${root}products/${item.handle}`
        : ''
    );

    if (!source) return '';

    const url = new URL(source, window.location.origin);

    if (url.origin !== window.location.origin) return '';

    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname
      .replace(/\.js$/, '')
      .replace(/\/$/, '')}.js`;

    return url.toString();
  } catch (error) {
    return '';
  }
}

formatMoney(cents, cart) {
  const amount = Number(cents || 0) / 100;
  const currency = cart.currency || window.Shopify?.currency?.active || '';
  const locale = document.documentElement.lang || navigator.language || 'en';

  let formattedAmount;

  try {
    formattedAmount = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(amount);
  } catch (error) {
    formattedAmount = amount.toFixed(2);
  }

  return currency
    ? `${formattedAmount} ${currency}`
    : formattedAmount;
}

formatCartTotal(cart) {
  return this.formatMoney(cart.total_price, cart);
}

    resizeImageUrl(
      source,
      width
    ) {
      try {
        const url = new URL(
          source,
          window.location.origin
        );

        url.searchParams.set(
          'width',
          width
        );

        return url.toString();
      } catch (error) {
        return source;
      }
    }

    prepareDrawerPosition() {
      this.restoreHeaderCartButton();

      if (
        !window.matchMedia(
          '(min-width: 640px)'
        ).matches
      ) {
        this.drawer.style.removeProperty(
          '--product-cart-notification-close-x'
        );

        this.drawer.style.removeProperty(
          '--product-cart-notification-close-y'
        );

        this.drawer.style.removeProperty(
          '--product-cart-notification-panel-top'
        );

        return;
      }

      const cartButton = Array.from(
        document.querySelectorAll(
          '.cart-drawer-button'
        )
      ).find((button) => {
        const rect =
          button.getBoundingClientRect();

        const style =
          window.getComputedStyle(button);

        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );
      });

      if (!cartButton) return;

      const rect =
        cartButton.getBoundingClientRect();

      const centerX =
        rect.left + rect.width / 2;

      const centerY =
        rect.top + rect.height / 2;

      const panelTop = Math.max(
        0,
        rect.top -
          Math.max(
            20,
            rect.height * 0.75
          )
      );

      this.drawer.style.setProperty(
        '--product-cart-notification-close-x',
        `${centerX}px`
      );

      this.drawer.style.setProperty(
        '--product-cart-notification-close-y',
        `${centerY}px`
      );

      this.drawer.style.setProperty(
        '--product-cart-notification-panel-top',
        `${panelTop}px`
      );

      cartButton.classList.add(
        'product-cart-notification__cart-trigger--hidden'
      );

      this.headerCartButton =
        cartButton;
    }

    restoreHeaderCartButton() {
      if (!this.headerCartButton) return;

      this.headerCartButton.classList.remove(
        'product-cart-notification__cart-trigger--hidden'
      );

      this.headerCartButton = null;
    }

    onDrawerAfterHide() {
      this.restoreHeaderCartButton();

      this.drawer.classList.remove(
        'is-content-ready'
      );
    }

    onResize() {
      if (!this.drawer?.open) return;

      window.requestAnimationFrame(() => {
        this.prepareDrawerPosition();
      });
    }

    clearError() {
      const errorElement =
        this.form.querySelector(
          '.product-form__error-message'
        );

      if (!errorElement) return;

      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    showError(message) {
      const errorElement =
        this.form.querySelector(
          '.product-form__error-message'
        );

      if (!errorElement) return;

      errorElement.textContent =
        message;

      errorElement.hidden = false;
    }
  }

  customElements.define(
    'product-cart-notification',
    ProductCartNotification
  );
})();
